import { cx } from "@/lib/cx.js";
import { avatarTone, initials, toneOf } from "@/lib/tokens.js";

/*
 * Small presentational pieces shared across pages: avatars, progress bars, stat
 * cards, empty states and tab strips. Ported from html-markup ui.js.
 */

/* ---- Avatar -------------------------------------------------------------- */

const AVATAR_SIZES = {
    xs: "size-5 text-[9px]",
    sm: "size-6 text-[9.5px]",
    md: "size-7 text-[10.5px]",
    lg: "size-8 text-[11px]",
    xl: "size-9 text-[12px]",
    "2xl": "size-16 rounded-2xl text-xl",
};

/** Colour is derived from the name, so a person is the same colour everywhere. */
export function Avatar({ name, size = "md", ring, className, ...rest }) {
    return (
        <span
            title={name}
            className={cx(
                "grid shrink-0 place-items-center rounded-full font-semibold text-white",
                AVATAR_SIZES[size] ?? AVATAR_SIZES.md,
                avatarTone(name),
                ring,
                className,
            )}
            {...rest}
        >
            {initials(name)}
        </span>
    );
}

/** Overlapping avatars with a "+N" bubble. */
export function AvatarStack({ people = [], max = 5, size = "md" }) {
    const shown = people.slice(0, max);
    const rest = people.length - shown.length;

    return (
        <span className="flex items-center -space-x-2">
            {shown.map((p) => (
                <Avatar
                    key={p.id ?? p.name}
                    name={p.name}
                    size={size}
                    className="ring-2 ring-white dark:ring-slate-900"
                />
            ))}
            {rest > 0 && (
                <span
                    className={cx(
                        "grid shrink-0 place-items-center rounded-full bg-slate-200 font-semibold text-slate-600 ring-2 ring-white dark:bg-slate-700 dark:text-slate-200 dark:ring-slate-900",
                        AVATAR_SIZES[size] ?? AVATAR_SIZES.md,
                    )}
                >
                    +{rest}
                </span>
            )}
        </span>
    );
}

/* ---- Progress ------------------------------------------------------------ */

/** Single-colour bar. `tone` is a token name, not a raw class. */
export function ProgressBar({ percent, tone = "brand", height = "h-1.5", className, title }) {
    const clamped = Math.max(0, Math.min(100, percent || 0));

    return (
        <span
            title={title}
            role="progressbar"
            aria-valuenow={Math.round(clamped)}
            aria-valuemin={0}
            aria-valuemax={100}
            className={cx(
                "flex w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800",
                height,
                className,
            )}
        >
            <span
                className={cx("h-full rounded-full transition-[width] duration-500", toneOf(tone).bar)}
                style={{ width: `${clamped}%` }}
            />
        </span>
    );
}

/**
 * Two-segment bar for work vs idle, as the dashboard's activity column used:
 * total width is the row's share of the largest row, split by work / idle.
 */
export function SplitBar({ totalPercent, firstShare, height = "h-1.5", title }) {
    const total = Math.max(0, Math.min(100, totalPercent || 0));
    const first = (total * Math.max(0, Math.min(100, firstShare || 0))) / 100;

    return (
        <span
            title={title}
            className={cx(
                "flex w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800",
                height,
            )}
        >
            <span className="h-full bg-emerald-500" style={{ width: `${first}%` }} />
            <span className="h-full bg-rose-500" style={{ width: `${total - first}%` }} />
        </span>
    );
}

/* ---- Stat card ----------------------------------------------------------- */

/**
 * The stat tile used across the dashboard, users, projects and reports pages.
 * `onClick` turns it into a button — several of them double as filter shortcuts.
 */
export function StatCard({ label, value, hint, tone = "slate", icon, onClick, active, className }) {
    const t = toneOf(tone);
    const Element = onClick ? "button" : "article";

    return (
        <Element
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={cx(
                "relative overflow-hidden rounded-xl border bg-white p-4 text-left shadow-xs transition dark:bg-slate-900",
                onClick && "hover:-translate-y-px hover:shadow-md",
                active
                    ? "border-brand-500 ring-2 ring-brand-500/20 dark:border-brand-600"
                    : "border-slate-200 dark:border-slate-800",
                className,
            )}
        >
            <span className={cx("absolute inset-y-0 left-0 w-0.5", t.edge)} aria-hidden="true" />

            <div className="flex items-center gap-2">
                {icon && (
                    <span className={cx("grid size-7 shrink-0 place-items-center rounded-lg", t.icon)}>
                        {icon}
                    </span>
                )}
                <span className="text-[12.5px] font-medium text-slate-600 dark:text-slate-400">
                    {label}
                </span>
            </div>

            <p
                className={cx(
                    "mt-2 truncate text-[22px] leading-none font-semibold tracking-tight",
                    t.text,
                )}
            >
                {value}
            </p>

            {hint && <p className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">{hint}</p>}
        </Element>
    );
}

/* ---- Empty state --------------------------------------------------------- */

// `...rest` so callers can attach data-* hooks and aria attributes. Without it
// they are silently dropped, which is how a test ends up asserting against an
// element that never had the attribute it was looking for.
export function EmptyState({ title, hint, action, icon, className, ...rest }) {
    return (
        <div
            {...rest}
            className={cx(
                "flex flex-col items-center gap-3 py-14 text-center text-slate-400 dark:text-slate-500",
                className,
            )}
        >
            {icon ?? (
                <svg
                    className="size-9 opacity-50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.2-3.2" />
                </svg>
            )}
            <p className="text-[13px]">{title}</p>
            {hint && <p className="max-w-sm text-[12px] leading-relaxed">{hint}</p>}
            {action}
        </div>
    );
}

/* ---- Tabs ---------------------------------------------------------------- */

/**
 * Segmented control — the pill style used for Grid/List, date presets and the
 * projects Active/Archived split.
 *
 *   <Segmented value={tab} onChange={setTab}
 *              options={[{ value: "grid", label: "Grid", icon, count }]} />
 */
export function Segmented({ value, onChange, options, className }) {
    return (
        <div className={cx("inline-flex flex-wrap gap-0.5 rounded-md bg-slate-200/70 p-0.5 dark:bg-slate-800", className)}>
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        data-segment={o.value}
                        onClick={() => onChange(o.value)}
                        aria-pressed={active}
                        className={cx(
                            "flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition",
                            active
                                ? "bg-white font-semibold text-slate-900 shadow-xs dark:bg-slate-700 dark:text-white"
                                : "font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white",
                        )}
                    >
                        {o.icon}
                        {o.label}
                        {o.count != null && <span className="opacity-60">{o.count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Underlined tab strip — the user-detail page's five tabs.
 * Rendered as a tablist so arrow-key navigation and screen readers work.
 */
export function Tabs({ value, onChange, options, className }) {
    return (
        <div role="tablist" className={cx("flex gap-1 overflow-x-auto px-3", className)}>
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        role="tab"
                        type="button"
                        aria-selected={active}
                        onClick={() => onChange(o.value)}
                        className={cx(
                            "shrink-0 border-b-2 px-3 py-2.5 text-[13px] transition",
                            active
                                ? "border-brand-600 font-semibold text-brand-600 dark:border-brand-400 dark:text-brand-400"
                                : "border-transparent font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
                        )}
                    >
                        {o.label}
                        {o.count != null && (
                            <span className="ml-1 rounded bg-slate-100 px-1.5 text-[11px] font-semibold dark:bg-slate-800">
                                {o.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

/* ---- Callout ------------------------------------------------------------- */

/**
 * The inline note used for the tracked/work/idle explanation, privacy warnings
 * and the retention warning. `tone` carries the meaning.
 */
const CALLOUT = {
    info: "border-brand-100 bg-brand-50/60 dark:border-brand-900 dark:bg-brand-950/40",
    warning: "border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30",
    danger: "border-rose-200 bg-rose-50/70 dark:border-rose-900 dark:bg-rose-950/30",
    success: "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/30",
    violet: "border-violet-200 bg-violet-50/70 dark:border-violet-900 dark:bg-violet-950/30",
};

const CALLOUT_ICON = {
    info: "text-brand-500",
    warning: "text-amber-500",
    danger: "text-rose-500",
    success: "text-emerald-500",
    violet: "text-violet-500",
};

export function Callout({ tone = "info", icon, className, children, ...rest }) {
    return (
        <div
            {...rest}
            className={cx(
                "flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5",
                CALLOUT[tone] ?? CALLOUT.info,
                className,
            )}
        >
            <span className={cx("mt-px shrink-0", CALLOUT_ICON[tone] ?? CALLOUT_ICON.info)}>
                {icon ?? (
                    <svg
                        className="size-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 16v-4M12 8h.01" />
                    </svg>
                )}
            </span>
            <div className="text-[12.5px] leading-relaxed text-slate-600 dark:text-slate-300">
                {children}
            </div>
        </div>
    );
}

/* ---- Definition list ----------------------------------------------------- */

/** dt/dd pair from index.css — System Information and the drawers use these. */
export function DefItem({ label, children, className }) {
    return (
        <div className={className}>
            <dt className="dt">{label}</dt>
            <dd className="dd">{children ?? <span className="text-slate-400 dark:text-slate-500">—</span>}</dd>
        </div>
    );
}
